import {
  PublicEventLocale,
  RegistrationAccessType,
  ScheduleItemV2,
  SpeakerV2,
  VenueType,
} from '@hopin-team/api-hopin';
import { Avatar } from '@hopin-team/ui-avatar';
import { Box } from '@hopin-team/ui-box';
import { Butticon } from '@hopin-team/ui-butticon';
import { Button } from '@hopin-team/ui-button';
import { Flex } from '@hopin-team/ui-flex';
import { Icon } from '@hopin-team/ui-icon';
import { Pill } from '@hopin-team/ui-pill';
import { Text } from '@hopin-team/ui-text';
import truncate from 'html-truncate';
import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { BREAKPOINT_MEDIUM_WIDTH } from '../../constants';
import {
  containsHTML,
  getAttendanceLabel,
  getEventPartLabel,
  getLocalisedDateTimeLabels,
  getVenueFlags,
  isSpeakersZoneRoute,
  parseHTML,
  shouldRenderAddToCalendar,
  TimeStatus,
} from '../../helpers';
import { useDateFormats, useDateLocale, useLocales } from '../../hooks';
import { AddToCalendarButton } from '../AddToCalendarButton';
import { EventPartIcon } from '../Icons';
import {
  eventAttendanceIcons,
  EventAttendanceTag,
  SpeakerContainer,
  TimeSplitter,
} from '.';
import { FormatTags } from './FormatTags';
import {
  ColumnOne,
  ColumnTwo,
  DayIncrement,
  Description,
  DescriptionSection,
  FullFlex,
  Link,
  MoreSpeakersButton,
  NowOnBadge,
  RenderAsHTML,
  Segment,
  Speakers,
  TagsSection,
  Title,
} from './item-styles';

const DEFAULT_SPEAKER_LIST_COUNT = 6;

export interface ItemComponentProps {
  item: ScheduleItemV2;
  client: string;
  eventLocale?: PublicEventLocale;
  eventDetails?: {
    app_title?: string;
    external_id?: string;
    custom_event_part_title?: string;
    custom_event_part_icon?: string;
    venue_type?: VenueType;
  };
  registrationAccessType?: RegistrationAccessType;
  onAddedToCalendar?: () => void;
  containerWidth?: number;
  showExternalIndicator?: boolean;
  showTags?: boolean;
  showSpeakers?: boolean;
  addToAgendaButton?: React.ReactNode;
  bookASeatButton?: React.ReactNode;
  onJoinEventPartClick?: () => void;
  setSpeakerModalProfile?: (profile: SpeakerV2 | null) => void;
  timeStatus?: TimeStatus;
  showRoom?: boolean;
  showCalendar?: boolean;
  calendarDetails?: {
    location: string;
    description: string;
    url: string;
  };
  /** @deprecated */
  showAttendanceBadge?: boolean;
  /** @deprecated */
  showNowBadge?: boolean;
  /** @deprecated */
  showEventPart?: boolean;
}

export const Item = ({
  item,
  eventLocale,
  eventDetails,
  onAddedToCalendar,
  containerWidth = 0,
  showTags = true,
  showSpeakers = true,
  showEventPart = true,
  showRoom = true,
  showAttendanceBadge = false,
  showNowBadge = false,
  showExternalIndicator = false,
  showCalendar = true,
  addToAgendaButton,
  calendarDetails,
  bookASeatButton,
  onJoinEventPartClick,
  setSpeakerModalProfile,
}: ItemComponentProps) => {
  const { t } = useLocales('event');
  const { t: addToCalendarT } = useLocales('addToCalendar');
  const { getFormattedTime } = useDateFormats();
  const locale = useDateLocale(navigator.language);
  const isSpeakersZonePage = isSpeakersZoneRoute();
  const { isVirtualEvent } = getVenueFlags(eventDetails?.venue_type);

  const {
    startDateTimeLabel,
    startTimeLabel,
    endTimeLabel,
    endTimeDaysIncrement,
  } = getLocalisedDateTimeLabels(item, locale, getFormattedTime);

  const [ref, inView] = useInView({
    threshold: 0,
    delay: 300,
    rootMargin: '1000px',
    triggerOnce: true,
  });

  const {
    speakers,
    tags,
    name,
    description,
    event_part_type,
    time_start,
    time_end,
    event_type,
    room,
  } = item;

  const [showAllSpeakers, setShowAllSpeakers] = useState(false);
  const speakersDisplayed = showAllSpeakers
    ? speakers
    : speakers.slice(0, DEFAULT_SPEAKER_LIST_COUNT);

  const [expandedDescription, setExpandedDescription] = useState(false);

  const displayedDescription =
    description && !expandedDescription
      ? truncate(description, 160, { ellipsis: '...' })
      : description;

  const showReadMore =
    description &&
    truncate(description, 160, { ellipsis: '...' }) !== description;

  const eventPartLabel = getEventPartLabel({
    t,
    eventPart: item.event_part_type,
    eventLocale,
    customEventPartTitle: eventDetails?.custom_event_part_title,
    appTitle: eventDetails?.app_title,
  });
  const isMedium = containerWidth <= BREAKPOINT_MEDIUM_WIDTH;
  // eslint-disable-next-line testing-library/render-result-naming-convention
  const shouldRenderCalendar = showCalendar && shouldRenderAddToCalendar(item);

  const handleSocialClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <ColumnOne isMedium={isMedium} mr={2}>
        <Flex
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="center"
          data-testid="event-part-time"
        >
          <Title mr={1} mb={0.75} pattern="headingFive">
            <time
              dateTime={time_start}
              data-local="time"
              data-format="%l:%M%p"
              title={startDateTimeLabel}
              data-localized=""
              aria-label={startTimeLabel}
            >
              {startTimeLabel}
            </time>
            <TimeSplitter>-</TimeSplitter>
            <time
              dateTime={time_end}
              data-local="time"
              data-format="%l:%M%p"
              title={startDateTimeLabel}
              data-localized=""
              aria-label={endTimeLabel}
            >
              {endTimeLabel}{' '}
              {endTimeDaysIncrement ? (
                <DayIncrement pattern="headingSix">
                  +{endTimeDaysIncrement}
                </DayIncrement>
              ) : (
                ''
              )}
            </time>
          </Title>
          {!isSpeakersZonePage && shouldRenderCalendar && (
            <div>
              <AddToCalendarButton
                label={addToCalendarT('title')}
                asIcon
                onAddedToCalendar={onAddedToCalendar}
                eventDetails={{
                  title: item.name,
                  startTime: item.time_start,
                  endTime: item.time_end,
                  description: calendarDetails?.description ?? '',
                  location: calendarDetails?.location ?? '',
                  url: calendarDetails?.url ?? '',
                }}
              />
            </div>
          )}
        </Flex>

        {!isVirtualEvent && (
          <Flex flexDirection="row" mb={0.5}>
            <FormatTags eventType={event_type} />
          </Flex>
        )}

        {event_part_type !== 'external_url' && showEventPart && (
          <Segment pattern="headingFive">
            <Flex
              flexDirection="row"
              alignItems="center"
              data-testid="event-part-type"
              className="event-part-type"
            >
              <EventPartIcon
                eventPartType={event_part_type}
                customEventPartIcon={eventDetails?.custom_event_part_icon}
                eventPartLabel={eventPartLabel}
                mr={1}
              />
              {eventPartLabel}
            </Flex>
          </Segment>
        )}

        {isSpeakersZonePage && (
          <div>
            <Box my={1}>
              <Button
                pattern="primary"
                size="small"
                onClick={onJoinEventPartClick}
              >
                {t('join-event-part', {
                  eventPartLabel,
                })}
              </Button>
            </Box>

            {shouldRenderCalendar && (
              <AddToCalendarButton
                label={addToCalendarT('title')}
                onAddedToCalendar={onAddedToCalendar}
                eventDetails={{
                  title: item.name,
                  startTime: item.time_start,
                  endTime: item.time_end,
                  description: calendarDetails?.description ?? '',
                  location: calendarDetails?.location ?? '',
                  url: calendarDetails?.url ?? '',
                }}
              />
            )}
          </div>
        )}

        {!isVirtualEvent && showRoom && event_type !== 'virtual' && (
          <Segment pattern="headingFive">
            <Flex
              flexDirection="row"
              alignItems="center"
              data-testid="room_name"
            >
              <Icon name="venue" color="grey-500" mr={1} />
              {room?.name || t('not-specified')}
            </Flex>
          </Segment>
        )}

        {showAttendanceBadge && item.attendance === 'recommended' && (
          <EventAttendanceTag
            onClick={() => ({})}
            attendance={item.attendance}
            isPressed
            label={t(getAttendanceLabel(item.attendance))}
            icon={eventAttendanceIcons[item.attendance].icon}
            my={1}
          />
        )}

        {bookASeatButton ? (
          bookASeatButton
        ) : addToAgendaButton ? (
          <Box mt={1}>{addToAgendaButton}</Box>
        ) : null}
      </ColumnOne>
      <ColumnTwo>
        <FullFlex justifyContent="space-between">
          <Flex flexDirection="row" alignItems="center" mb={0.5}>
            {showNowBadge && (
              <NowOnBadge role="img" data-testid="now-on-badge">
                {t('now')}
              </NowOnBadge>
            )}

            <Title pattern="headingFour">{name}</Title>
          </Flex>
          <Flex alignItems="center">
            {showExternalIndicator && (
              <Icon
                data-testid="external-url-icon"
                name="open-in-new"
                color="grey-600"
                ml={1}
              />
            )}
            {bookASeatButton && addToAgendaButton}
          </Flex>
        </FullFlex>

        {description && (
          <DescriptionSection justifyContent="space-between">
            <Flex>
              <Description pattern="body">
                {displayedDescription && containsHTML(displayedDescription) ? (
                  <RenderAsHTML
                    data-testid="description-html"
                    dangerouslySetInnerHTML={{
                      __html: parseHTML(displayedDescription),
                    }}
                  />
                ) : (
                  displayedDescription
                )}
              </Description>
            </Flex>
            {showReadMore && (
              <Flex flexBasis="auto">
                <Butticon
                  title={t('show-more-description')}
                  icon={expandedDescription ? 'arrow-up' : 'arrow-down'}
                  onClick={e => {
                    e.stopPropagation();
                    setExpandedDescription(!expandedDescription);
                  }}
                  data-testid="toggle-expanded-description"
                />
              </Flex>
            )}
          </DescriptionSection>
        )}

        {showTags && tags.length > 0 && (
          <TagsSection className="schedule-event-list-item-tags" mt={2}>
            {tags.map(tag => (
              <Pill key={`tag-${tag.name}`} label={tag.name} mr={1} mb={1} />
            ))}
          </TagsSection>
        )}

        {showSpeakers && speakersDisplayed.length > 0 && (
          <FullFlex ref={ref}>
            <Speakers data-gridcols={2} mt={3} isMedium={isMedium}>
              {speakersDisplayed.map(speaker => {
                const [firstName, lastName] = speaker.name.split(' ');
                return (
                  <SpeakerContainer
                    link={
                      !!speaker.user_external_id && !!setSpeakerModalProfile
                    }
                    key={`avatar-${firstName}-${lastName}`}
                    data-testid="speaker-profile"
                    onClick={() => {
                      speaker.user_external_id &&
                        setSpeakerModalProfile?.(speaker);
                    }}
                  >
                    {inView ? (
                      <Flex flexBasis="auto">
                        <Avatar
                          alt=""
                          size="medium"
                          firstName={firstName}
                          lastName={lastName}
                          src={speaker.thumb_picture_url}
                          mr={1}
                        />
                      </Flex>
                    ) : (
                      <Flex px={3} py={2.5} flexBasis="auto" />
                    )}
                    <Flex
                      justifyContent="flex-start"
                      flexDirection="column"
                      mr={2}
                    >
                      <Text pattern="headingFive">{speaker.name}</Text>
                      <Text pattern="body">{speaker.headline}</Text>
                      <Flex
                        justifyContent="flex-start"
                        flexDirection="row"
                        ml={-0.5}
                      >
                        {speaker.linkedin && (
                          <Link
                            href={speaker.linkedin}
                            onClick={handleSocialClick}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`linkedin-icon-${speaker.name
                              .toLowerCase()
                              .replace(/\s/, '-')}`}
                            title="LinkedIn"
                            icon="linkedin"
                            mr={1}
                          />
                        )}
                        {speaker.twitter && (
                          <Link
                            href={speaker.twitter}
                            onClick={handleSocialClick}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`twitter-icon-${speaker.name
                              .toLowerCase()
                              .replace(/\s/, '-')}`}
                            title="Twitter"
                            icon="twitter"
                            mr={1}
                          />
                        )}
                        {speaker.website && (
                          <Link
                            href={speaker.website}
                            onClick={handleSocialClick}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`website-icon-${speaker.name
                              .toLowerCase()
                              .replace(/\s/, '-')}`}
                            title={t('website')}
                            icon="website"
                            mr={1}
                          />
                        )}
                      </Flex>
                    </Flex>
                  </SpeakerContainer>
                );
              })}
            </Speakers>
            {speakers.length > DEFAULT_SPEAKER_LIST_COUNT &&
              !showAllSpeakers && (
                <MoreSpeakersButton
                  onClick={e => {
                    e.stopPropagation();
                    setShowAllSpeakers(true);
                  }}
                >
                  {t('view-more-speakers', {
                    count: speakers.length - DEFAULT_SPEAKER_LIST_COUNT,
                  })}
                </MoreSpeakersButton>
              )}
          </FullFlex>
        )}
      </ColumnTwo>
    </>
  );
};
